import React, { useMemo, useState } from "react";
import { fmtNum, positionColor, trendColor } from "@/lib/format";
import { Search, ArrowUp, ArrowDown } from "lucide-react";

const POS_FILTERS = ["ALL", "QB", "RB", "WR", "TE", "PICK"];

export function RankingsTable({ players, fpCount }) {
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("ALL");
  const [sortKey, setSortKey] = useState("combinedRank");
  const [sortDir, setSortDir] = useState("asc");

  const positionRanks = useMemo(() => {
    const ranks = new Map();
    const positionCounts = new Map();
    const sortedPlayers = [...players]
      .filter((player) => player.position && player.position !== "PICK")
      .sort((a, b) => (b.combinedValue || 0) - (a.combinedValue || 0));

    for (const player of sortedPlayers) {
      const nextRank = (positionCounts.get(player.position) || 0) + 1;
      positionCounts.set(player.position, nextRank);
      ranks.set(String(player.id), `${player.position}${nextRank}`);
    }
    return ranks;
  }, [players]);

  const rows = useMemo(() => {
    let r = players;
    if (pos !== "ALL") r = r.filter(p => p.position === pos);
    if (q.trim()) {
      const t = q.toLowerCase();
      r = r.filter(p => p.name.toLowerCase().includes(t) || (p.team || "").toLowerCase().includes(t));
    }
    const sorted = [...r].sort((a, b) => {
      const va = a[sortKey] ?? -Infinity;
      const vb = b[sortKey] ?? -Infinity;
      if (typeof va === "string") {
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return sorted.slice(0, 300);
  }, [players, q, pos, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "name" ? "asc" : "asc"); }
  };

  const SortHeader = ({ k, label, align = "left" }) => (
    <th className={`px-3 py-2 text-${align} label-eyebrow cursor-pointer select-none whitespace-nowrap`} onClick={() => toggleSort(k)} data-testid={`rankings-sort-${k}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </span>
    </th>
  );

  return (
    <section className="mt-16" data-testid="rankings-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">05 / Rankings</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">The <span className="text-[#7C3AED]">TRYSTOMATIC</span> board.</h2>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
            Our proprietary dynasty value — one number per asset, refreshed every page load.
          </p>
        </div>
      </div>

      <div className="brutal-card">
        <div className="border-b border-[#0A0A0A] p-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search player or team…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-[#0A0A0A]/20 focus:border-[#0A0A0A] outline-none font-mono-data"
              data-testid="rankings-search"
            />
          </div>
          <div className="flex items-center gap-1" data-testid="rankings-pos-filters">
            {POS_FILTERS.map((p) => (
              <button
                key={p}
                onClick={() => setPos(p)}
                className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${pos === p ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#0A0A0A]/20 hover:border-[#0A0A0A]"}`}
                data-testid={`rankings-pos-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-auto max-h-[680px]">
          <table className="w-full text-sm border-collapse" data-testid="rankings-table">
            <thead className="sticky top-0 bg-white z-10 border-b border-[#0A0A0A]">
              <tr>
                <SortHeader k="combinedRank" label="#" />
                <th className="px-3 py-2 text-left label-eyebrow whitespace-nowrap">POS RK</th>
                <SortHeader k="name" label="Player" />
                <th className="px-3 py-2 text-left label-eyebrow">Pos</th>
                <th className="px-3 py-2 text-left label-eyebrow">Team</th>
                <SortHeader k="age" label="Age" align="right" />
                <SortHeader k="combinedValue" label="Value" align="right" />
                <SortHeader k="trend30Day" label="30d" align="right" />
              </tr>
            </thead>
            <tbody>
              {rows.map((p, idx) => (
                <tr key={p.id} className={`border-b border-[#E4E4E7] ${idx % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"} hover:bg-[#F3F4F6]`} data-testid={`row-${p.id}`}>
                  <td className="px-3 py-2 font-mono-data tabular-nums text-[#52525B]">{p.combinedRank}</td>
                  <td className="px-3 py-2 font-mono-data tabular-nums text-[#52525B]">{p.position === "PICK" ? "" : positionRanks.get(String(p.id))}</td>
                  <td className="px-3 py-2 font-medium">{p.name}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 ${positionColor(p.position)}`}>{p.position}</span></td>
                  <td className="px-3 py-2 font-mono-data text-xs text-[#52525B]">{p.team || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#52525B]">{p.age ? Number(p.age).toFixed(1) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular-nums font-semibold">{fmtNum(p.combinedValue)}</td>
                  <td className={`px-3 py-2 text-right font-mono-data tabular-nums ${trendColor(p.trend30Day)}`}>
                    {p.trend30Day > 0 ? "+" : ""}{fmtNum(p.trend30Day)}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan="8" className="px-3 py-8 text-center text-[#52525B] text-sm">No players match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
